import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { makeCustomDecimalNumberMask } from '@app/functions';
import { formatLocalizedNumber, Locale, parseLocalizedNumber, TranslatableKey } from '@app/types/translatable.type';
import { TimeOffPolicy } from '@models/time-off-v3/time-off-policy.model';
import { TimeOffSeniorityOverride } from '@models/time-off-v3/time-off-seniority-override.model';
import { TranslateService } from '@ngx-translate/core';
import { getTime, setTime } from '@time-off-v3/functions/time-unit-converter';
import { AccrualFrequencies, DECIMAL_PLACES } from '@time-off-v3/meta/time-off-meta';
import { PolicyManageService } from '@time-off-v3/services/policy-v2/policy-manage.service';
import cloneDeep from 'lodash-es/cloneDeep';

@Component({
    selector: 'app-amount-accrued-per-year-update-card',
    templateUrl: './amount-accrued-per-year-update-card.template.html',
    styleUrls: ['./amount-accrued-per-year-update-card.style.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class AmountAccruedPerYearUpdateCardComponent implements OnInit {
    @Input() timeOffPolicy = new TimeOffPolicy();
    @Input() unitOfTime: TranslatableKey = 'time-off-v3.days';
    @Output() onChange = new EventEmitter();
    @Output() onSeniorityOverrideChange = new EventEmitter<TimeOffSeniorityOverride[]>();

    isLoading = false;

    timeOffSeniorityOverrides: TimeOffSeniorityOverride[] = [];
    timeOffSeniorityOverridesCache: TimeOffSeniorityOverride[] = [];
    timeOffPolicyCache: TimeOffPolicy;

    accrualFrequencies = AccrualFrequencies;
    days = 0;

    numberMask = makeCustomDecimalNumberMask(this.translateService.currentLang as Locale);

    constructor(
        private policyManageService: PolicyManageService,
        private translateService: TranslateService
    ) {}

    formatLocalizedNumber = (value: number): string | undefined =>
        formatLocalizedNumber(value, { locale: this.translateService.currentLang as Locale });
    parseLocalizedNumber = (value: string): number =>
        parseLocalizedNumber(value, this.translateService.currentLang as Locale);

    ngOnInit(): void {
        this.timeOffSeniorityOverrides = this.timeOffPolicy.timeOffSeniorityOverrides;
        this.timeOffPolicyCache = cloneDeep(this.timeOffPolicy);
        // We cache the initial seniority overrides for delete comparison
        this.timeOffSeniorityOverridesCache = cloneDeep(this.timeOffSeniorityOverrides);

        this.setDaysPerYear();
    }

    onAmountAccruedPerYearChange(): void {
        if (this.days !== null && this.timeOffPolicy.accrualFrequency) {
            const daysPerPeriod = this.days / this.accrualFrequencies[this.timeOffPolicy.accrualFrequency];
            this.timeOffPolicy.daysAccruedPerPeriod = setTime(daysPerPeriod, this.timeOffPolicy?.timeOffType);
            this.policyManageService.daysAccruedPerPeriodChangedSubject$.next(true);
            this.onChange.emit();
        }
    }

    addSeniorityOverride(): void {
        this.timeOffSeniorityOverrides.push(new TimeOffSeniorityOverride());
        this.onChange.emit();
        this.onSeniorityOverrideChange.emit(this.timeOffSeniorityOverrides);
    }

    removeSeniorityOverride(index: number): void {
        this.timeOffSeniorityOverrides.splice(index, 1);
        this.onSeniorityOverrideChange.emit(this.timeOffSeniorityOverrides);
    }

    getAccruedValuePerPeriod(accruedValue: number): number | null | undefined {
        return getTime(accruedValue, this.timeOffPolicy?.timeOffType);
    }

    setDaysAccruedPerPeriod(seniorityOverride: TimeOffSeniorityOverride, val: number): void {
        if (!this.timeOffPolicy.accrualFrequency) {
            return;
        }

        seniorityOverride.daysAccruedPerPeriod =
            setTime(val, this.timeOffPolicy.timeOffType) / AccrualFrequencies[this.timeOffPolicy.accrualFrequency];

        this.onChange.emit();
    }

    getDaysAccruedPerPeriod(seniorityOverride: TimeOffSeniorityOverride): number {
        if (!this.timeOffPolicy.accrualFrequency) {
            return null;
        }

        // If accrual frequency changes we need to recalculate the amount
        if (this.timeOffPolicyCache.accrualFrequency !== this.timeOffPolicy.accrualFrequency) {
            const totalAmountPriorToChange =
                getTime(seniorityOverride.daysAccruedPerPeriod, this.timeOffPolicy.timeOffType) *
                AccrualFrequencies[this.timeOffPolicyCache.accrualFrequency];
            this.setDaysAccruedPerPeriod(seniorityOverride, totalAmountPriorToChange);
            this.timeOffPolicyCache = cloneDeep(this.timeOffPolicy);
        }

        if (seniorityOverride.daysAccruedPerPeriod === undefined) {
            return null;
        }

        const convertAccrualPerPeriodOnUnitOfTime = getTime(
            seniorityOverride.daysAccruedPerPeriod,
            this.timeOffPolicy.timeOffType
        );

        return +(convertAccrualPerPeriodOnUnitOfTime * AccrualFrequencies[this.timeOffPolicy.accrualFrequency]).toFixed(
            DECIMAL_PLACES
        );
    }

    private setDaysPerYear(): void {
        // Both of these can be undefined in the beginning so we don't want to do a calculation with them
        if (
            this.timeOffPolicy.accrualFrequency === undefined ||
            this.timeOffPolicy.daysAccruedPerPeriod === undefined
        ) {
            this.days = null;
            return;
        }

        this.days = +getTime(
            +(this.timeOffPolicy.daysAccruedPerPeriod * this.accrualFrequencies[this.timeOffPolicy.accrualFrequency]),
            this.timeOffPolicy.timeOffType
        ).toFixed(DECIMAL_PLACES);
    }
}
